import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  Text,
  VStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
  Icon,
  Flex,
  useColorModeValue,
  Button,
  Image,
  HStack,
  Divider,
  useBreakpointValue,
} from '@chakra-ui/react';
import { FiShield, FiHeart, FiUmbrella, FiArrowRight } from 'react-icons/fi';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

const INSURANCE_PRODUCTS = {
  lifeInsurance: {
    title: 'Life Insurance',
    icon: FiShield,
    color: 'blue',
    description: "Secure your family's future with our comprehensive life insurance plans.",
    image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&q=80&w=1000",
    companies: [
      {
        name: 'Reliance Nippon',
        products: ['Term Life Insurance', 'Whole Life Insurance', 'ULIP Plans'],
        badge: 'Popular',
        highlights: ['Up to ₹1 Crore Coverage', 'Tax Benefits', 'Flexible Payment Options']
      },
      {
        name: 'Axis Max',
        products: ['Term Insurance', 'Retirement Plans', 'Child Plans'],
        highlights: ['Low Premium Rates', 'Multiple Payout Options', '24x7 Support']
      },
      {
        name: 'Future Generali',
        products: ['Term Life Coverage', 'Savings Plans', 'Group Insurance'],
        highlights: ['Quick Claim Settlement', 'Worldwide Coverage', 'No Medical Test*']
      }
    ]
  },
  healthInsurance: {
    title: 'Health Insurance',
    icon: FiHeart,
    color: 'red',
    description: 'Protect yourself and your loved ones with our health insurance plans.',
    image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&q=80&w=1000",
    companies: [
      {
        name: 'Tata AIG',
        products: ['Individual Health Insurance', 'Family Floater', 'Critical Illness Cover'],
        badge: 'Recommended',
        highlights: ['Cashless Treatment', 'No Claim Bonus', 'Pre-existing Disease Cover']
      }
    ]
  },
  generalInsurance: {
    title: 'General Insurance',
    icon: FiUmbrella,
    color: 'green',
    description: 'Protect your assets with our comprehensive general insurance solutions.',
    image: "https://images.unsplash.com/photo-1582559934353-2e47140e7501?auto=format&fit=crop&q=80&w=1000",
    companies: [
      {
        name: 'Multiple Companies',
        products: ['Motor Insurance', 'Home Insurance', 'Travel Insurance', 'Property Insurance'],
        badge: 'All-in-One',
        highlights: ['Instant Policy', 'Digital Claims', 'Wide Network']
      }
    ]
  }
};

const Products = () => {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const bgColor = useColorModeValue('gray.50', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const isDesktop = useBreakpointValue({ base: false, lg: true });

  const FeatureCard = ({ company, color }) => (
    <MotionBox
      whileHover={{ y: -5 }}
      p={6}
      bg={cardBg}
      shadow="md"
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="xl"
      transition="all 0.3s"
      cursor="pointer"
      onClick={() => setSelectedCompany(company)}
      position="relative"
      overflow="hidden"
    >
      <Flex direction="column" height="100%">
        <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={2}>
          <Heading size="md" mr={2}>{company.name}</Heading>
          {company.badge && (
            <Badge
              colorScheme={color}
              borderRadius="full"
              px={3}
              py={1}
              fontSize="sm"
              whiteSpace="nowrap"
            >
              {company.badge}
            </Badge>
          )}
        </Flex>

        <VStack align="stretch" spacing={4}>
          <Box>
            <Text fontWeight="bold" mb={2}>Key Features:</Text>
            {company.highlights.map((highlight, index) => (
              <Text key={index} fontSize="sm" color="gray.600" _dark={{ color: 'gray.300' }}>
                • {highlight}
              </Text>
            ))}
          </Box>

          <Divider />

          <Box>
            <Text fontWeight="bold" mb={2}>Available Plans:</Text>
            {company.products.map((product, index) => (
              <Text key={index} fontSize="sm" color="gray.600" _dark={{ color: 'gray.300' }}>
                • {product}
              </Text>
            ))}
          </Box>
        </VStack>
      </Flex>
    </MotionBox>
  );

  return (
    <Box bg={bgColor} minH="100vh" py={12}>
      <Container maxW="container.xl">
        <VStack spacing={12} align="stretch">
          <MotionFlex
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            direction="column"
            align="center"
            textAlign="center"
            pb={10}
          >
            <Heading
              as="h1"
              size="2xl"
              mb={4}
              bgGradient="linear(to-r, blue.400, purple.500)"
              bgClip="text"
            >
              Insurance Solutions
            </Heading>
            <Text fontSize="xl" color="gray.500" maxW="container.md">
              Protect what matters most with our comprehensive range of insurance products
            </Text>
          </MotionFlex>

          <Tabs variant="soft-rounded" colorScheme="blue" isLazy>
            <TabList 
              overflowX="auto" 
              py={4}
              css={{
                scrollbarWidth: 'none',
                '::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {Object.entries(INSURANCE_PRODUCTS).map(([key, category]) => (
                <Tab
                  key={key}
                  mx={2}
                  px={6}
                  py={3}
                  borderRadius="full"
                  _selected={{
                    color: `${category.color}.800`,
                    bg: `${category.color}.100`,
                  }}
                >
                  <HStack spacing={2}>
                    <Icon as={category.icon} />
                    <Text>{category.title}</Text>
                  </HStack>
                </Tab>
              ))}
            </TabList>

            <TabPanels>
              {Object.entries(INSURANCE_PRODUCTS).map(([key, category]) => (
                <TabPanel key={key} px={0}>
                  <MotionFlex
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    direction={{ base: 'column', lg: 'row' }}
                    gap={8}
                    align="stretch"
                  >
                    {isDesktop && (
                      <Box flex="1" position="relative" height="auto">
                        <Image
                          src={category.image}
                          alt={category.title}
                          objectFit="cover"
                          borderRadius="xl"
                          height="400px"
                          width="100%"
                          fallbackSrc="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&q=80&w=1000"
                          loading="lazy"
                        />
                        <Box
                          position="absolute"
                          bottom={0}
                          left={0}
                          right={0}
                          p={6}
                          background="linear-gradient(to top, rgba(0,0,0,0.7), transparent)"
                          borderBottomRadius="xl"
                        >
                          <Text color="white" fontSize="lg">
                            {category.description}
                          </Text>
                        </Box>
                      </Box>
                    )}

                    <Box flex="2">
                      <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={6}>
                        {category.companies.map((company) => (
                          <FeatureCard
                            key={company.name}
                            company={company}
                            color={category.color}
                          />
                        ))}
                      </SimpleGrid>
                    </Box>
                  </MotionFlex>
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        </VStack>
      </Container>
    </Box>
  );
};

export default Products;